import React, { useEffect, useState, useCallback, useRef } from 'react';
import { escrowAPI } from '../../api/api';
import { toast } from '../../hooks/use-toast';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Warning,
  ShieldCheck,
  Camera,
  PaperPlaneTilt,
  SpinnerGap,
  Clock,
  CheckCircle,
  Scales,
  User as UserIcon,
  X,
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PHASE_LABEL = {
  awaiting_response: 'Awaiting other party response',
  awaiting_admin: 'Awaiting admin review',
  resolved: 'Resolved',
};

const PHASE_TONE = {
  awaiting_response: 'bg-amber-50 border-amber-300 text-amber-800',
  awaiting_admin: 'bg-blue-50 border-blue-300 text-blue-800',
  resolved: 'bg-emerald-50 border-emerald-300 text-emerald-800',
};

function EvidenceThumbList({ urls }) {
  if (!urls || urls.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {urls.map((u, i) => {
        const isPdf = u.toLowerCase().endsWith('.pdf');
        const href = u.startsWith('http') ? u : `${API_URL}${u}`;
        return (
          <a
            key={i}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="block w-20 h-20 border-2 border-black rounded-lg overflow-hidden bg-white relative hover:shadow-[3px_3px_0px_#000] transition-all"
            data-testid={`evidence-thumb-${i}`}
          >
            {isPdf ? (
              <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 text-red-700">
                <Camera size={18} weight="bold" />
                <span className="text-[9px] font-black mt-1">PDF</span>
              </div>
            ) : (
              <img src={href} alt="evidence" className="w-full h-full object-cover" />
            )}
          </a>
        );
      })}
    </div>
  );
}

function EvidenceUploader({ urls, onChange, max = 6, testidPrefix = 'evidence' }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    if (urls.length + arr.length > max) {
      toast({ title: `Max ${max} files`, description: 'Remove a file before adding more.', variant: 'destructive' });
      return;
    }
    setUploading(true);
    const next = [...urls];
    for (const f of arr) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const resp = await escrowAPI.uploadEvidence(fd);
        if (resp.data?.url) next.push(resp.data.url);
      } catch (e) {
        toast({ title: 'Upload failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      }
    }
    onChange(next);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {urls.map((u, i) => {
          const isPdf = u.toLowerCase().endsWith('.pdf');
          const href = u.startsWith('http') ? u : `${API_URL}${u}`;
          return (
            <div key={i} className="relative w-20 h-20 border-2 border-black rounded-lg overflow-hidden bg-white" data-testid={`${testidPrefix}-preview-${i}`}>
              {isPdf ? (
                <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 text-red-700">
                  <Camera size={18} weight="bold" />
                  <span className="text-[9px] font-black mt-1">PDF</span>
                </div>
              ) : (
                <img src={href} alt="evidence" className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={() => onChange(urls.filter((_, idx) => idx !== i))}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full border-2 border-black flex items-center justify-center hover:bg-red-600"
                aria-label="Remove evidence"
                data-testid={`${testidPrefix}-remove-${i}`}
              >
                <X size={12} weight="bold" />
              </button>
            </div>
          );
        })}
        {urls.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current && inputRef.current.click()}
            disabled={uploading}
            className="w-20 h-20 border-2 border-dashed border-neutral-400 rounded-lg flex flex-col items-center justify-center text-neutral-500 hover:text-neutral-900 hover:border-neutral-900 transition-all"
            data-testid={`${testidPrefix}-add-btn`}
          >
            {uploading ? <SpinnerGap size={22} className="animate-spin" /> : <><Camera size={22} weight="bold" /><span className="text-[10px] font-bold mt-1">Add</span></>}
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid={`${testidPrefix}-file-input`}
      />
      <p className="text-xs text-neutral-500 mt-2">Photos or PDFs · up to 8 MB each · max {max} files</p>
    </div>
  );
}

export default function DisputeCenter({ escrow, currentUserId, onUpdate }) {
  const [caseFile, setCaseFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showResponse, setShowResponse] = useState(false);
  const [statement, setStatement] = useState('');
  const [responseEvidence, setResponseEvidence] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const escrowId = escrow?._id || escrow?.id;

  const fetchCase = useCallback(async () => {
    if (!escrowId) return;
    try {
      setLoading(true);
      const r = await escrowAPI.getDisputeCaseFile(escrowId);
      if (r.data?.has_dispute) setCaseFile(r.data);
      else setCaseFile(null);
    } catch (e) {
      // silent if no dispute yet
    } finally {
      setLoading(false);
    }
  }, [escrowId]);

  useEffect(() => { fetchCase(); }, [fetchCase]);

  if (loading) {
    return (
      <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] mb-6 flex items-center gap-3 text-neutral-600" data-testid="dispute-center-loading">
        <SpinnerGap size={22} className="animate-spin" />
        Loading dispute case file…
      </div>
    );
  }

  if (!caseFile) return null;

  const isContractor = currentUserId === escrow?.contractor_id;
  const isWorker = currentUserId === escrow?.worker_id;
  const raisedByMe = caseFile.disputed_by === currentUserId;
  const canRespond = (isContractor || isWorker)
    && caseFile.dispute_phase === 'awaiting_response'
    && !raisedByMe;

  const handleSubmitResponse = async () => {
    if (!statement.trim()) {
      toast({ title: 'Add a statement', description: 'Briefly explain your side before submitting.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      await escrowAPI.respondToDispute(escrowId, {
        statement: statement.trim(),
        evidence_urls: responseEvidence,
      });
      toast({ title: 'Response submitted', description: 'Our team will now review the case.' });
      setShowResponse(false);
      setStatement('');
      setResponseEvidence([]);
      await fetchCase();
      onUpdate && onUpdate();
    } catch (e) {
      toast({ title: 'Failed to submit', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const phase = caseFile.dispute_phase || 'awaiting_admin';

  return (
    <div className="bg-white border-2 border-black rounded-xl shadow-[4px_4px_0px_#000] mb-6 overflow-hidden" data-testid="dispute-center">
      {/* Header */}
      <div className="bg-red-50 border-b-2 border-black px-6 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Warning size={26} weight="fill" className="text-red-600" />
          <div>
            <h2 className="text-lg font-black text-red-700">Dispute Center</h2>
            <p className="text-xs text-red-600">Active dispute on this escrow</p>
          </div>
        </div>
        <span
          className={`text-[11px] font-black uppercase tracking-wider px-3 py-1 rounded-full border-2 ${PHASE_TONE[phase] || 'bg-neutral-100 border-neutral-300 text-neutral-700'}`}
          data-testid="dispute-phase-badge"
        >
          {PHASE_LABEL[phase] || phase}
        </span>
      </div>

      {/* Body */}
      <div className="p-6 space-y-6">
        {/* Opener statement */}
        <section data-testid="dispute-opener-statement">
          <div className="flex items-center gap-2 mb-2">
            <UserIcon size={18} weight="bold" className="text-neutral-500" />
            <p className="text-xs font-black uppercase tracking-wider text-neutral-500">
              Filed by the {caseFile.disputed_by_role}
              {caseFile.disputed_at && (
                <span className="font-normal normal-case text-neutral-400 ml-1">
                  · {new Date(caseFile.disputed_at).toLocaleString()}
                </span>
              )}
            </p>
          </div>
          <div className="bg-neutral-50 border-2 border-neutral-200 rounded-lg p-4">
            <p className="text-sm font-bold text-neutral-900 mb-1">Reason: {caseFile.reason?.replace(/_/g, ' ')}</p>
            <p className="text-sm text-neutral-700 whitespace-pre-wrap">{caseFile.description}</p>
            <EvidenceThumbList urls={caseFile.evidence_urls} />
          </div>
        </section>

        {/* Response (if exists) */}
        {caseFile.response_statement && (
          <section data-testid="dispute-response-statement">
            <div className="flex items-center gap-2 mb-2">
              <UserIcon size={18} weight="bold" className="text-blue-500" />
              <p className="text-xs font-black uppercase tracking-wider text-blue-700">
                Response from other party
                {caseFile.response_at && (
                  <span className="font-normal normal-case text-blue-400 ml-1">
                    · {new Date(caseFile.response_at).toLocaleString()}
                  </span>
                )}
              </p>
            </div>
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <p className="text-sm text-neutral-700 whitespace-pre-wrap">{caseFile.response_statement}</p>
              <EvidenceThumbList urls={caseFile.response_evidence_urls} />
            </div>
          </section>
        )}

        {/* Awaiting message */}
        {phase === 'awaiting_response' && raisedByMe && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 flex items-start gap-3" data-testid="awaiting-other-banner">
            <Clock size={22} weight="bold" className="text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-900">
              <p className="font-black">Waiting on the other party's response</p>
              <p className="mt-1">
                They have 48 hours to submit their statement and evidence. If they don't respond, our team will still review and resolve.
                {caseFile.response_deadline && (
                  <span className="block mt-1 text-amber-700">
                    Deadline: {new Date(caseFile.response_deadline).toLocaleString()}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {phase === 'awaiting_admin' && (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 flex items-start gap-3" data-testid="awaiting-admin-banner">
            <Scales size={22} weight="bold" className="text-blue-700 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-black">Under admin review</p>
              <p className="mt-1">
                Our team is reviewing both statements and evidence. Resolution typically within 24–48 hours. You'll be notified by email and in-app when a decision is made.
              </p>
            </div>
          </div>
        )}

        {phase === 'resolved' && (
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4" data-testid="resolution-banner">
            <div className="flex items-start gap-3">
              <CheckCircle size={22} weight="fill" className="text-emerald-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-emerald-900">
                <p className="font-black">Dispute resolved</p>
                <p className="mt-1">
                  Resolution: <span className="font-bold">{(caseFile.resolution_type || '').replace(/_/g, ' ')}</span>
                  {caseFile.resolved_at && (
                    <span className="text-emerald-700 ml-1">· {new Date(caseFile.resolved_at).toLocaleString()}</span>
                  )}
                </p>
                {caseFile.resolution_note && (
                  <p className="mt-2 italic text-emerald-800">"{caseFile.resolution_note}"</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Response form */}
        {canRespond && !showResponse && (
          <button
            type="button"
            onClick={() => setShowResponse(true)}
            className="w-full h-12 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
            data-testid="open-dispute-response-btn"
          >
            <PaperPlaneTilt size={20} weight="bold" />
            Submit Your Response
          </button>
        )}

        {canRespond && showResponse && (
          <div className="border-2 border-[#2B4C6F] rounded-xl p-4 bg-blue-50/30" data-testid="dispute-response-form">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-black text-[#2B4C6F] uppercase tracking-wider">Your Response</h3>
              <button onClick={() => setShowResponse(false)} className="text-neutral-400 hover:text-neutral-700">
                <X size={20} weight="bold" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="font-bold mb-2 block">Your statement</Label>
                <Textarea
                  value={statement}
                  onChange={(e) => setStatement(e.target.value)}
                  rows={5}
                  placeholder="Explain your side of the story, including dates, what was agreed to, and what actually happened…"
                  className="border-2 border-black rounded-xl"
                  data-testid="response-statement-input"
                />
              </div>
              <div>
                <Label className="font-bold mb-2 block">Evidence (optional)</Label>
                <EvidenceUploader
                  urls={responseEvidence}
                  onChange={setResponseEvidence}
                  max={6}
                  testidPrefix="response-evidence"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResponse(false)}
                  className="flex-1 h-12 bg-neutral-100 text-neutral-700 font-bold rounded-xl border-2 border-neutral-300"
                  data-testid="cancel-response-btn"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitResponse}
                  disabled={submitting}
                  className="flex-1 h-12 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold rounded-xl border-2 border-black disabled:opacity-50"
                  data-testid="submit-response-btn"
                >
                  {submitting ? <SpinnerGap size={20} className="animate-spin" /> : <><PaperPlaneTilt size={18} weight="bold" /> Submit Response</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        {caseFile.events && caseFile.events.length > 0 && (
          <section data-testid="dispute-timeline">
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheck size={18} weight="bold" className="text-neutral-500" />
              <p className="text-xs font-black uppercase tracking-wider text-neutral-500">Case timeline</p>
            </div>
            <ol className="border-l-2 border-neutral-200 ml-2 space-y-3">
              {caseFile.events.map((ev) => (
                <li key={ev.event_id || ev.timestamp} className="pl-4 relative">
                  <span className="absolute -left-[7px] top-1.5 w-3 h-3 bg-neutral-400 rounded-full border-2 border-white" />
                  <p className="text-xs text-neutral-500">
                    {new Date(ev.timestamp).toLocaleString()} · <span className="font-bold text-neutral-700">{ev.actor_name}</span> ({ev.actor_role})
                  </p>
                  <p className="text-sm text-neutral-800 font-medium capitalize">{(ev.type || '').replace(/_/g, ' ')}</p>
                </li>
              ))}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}

export { EvidenceUploader };
